import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  SafeAreaView,
  Pressable,
  RefreshControl,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
  Modal,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { useWatchlist } from '@hooks/useWatchlist';
import { useMarketData } from '@hooks/useMarketData';
import { useStockSparklines } from '@hooks/useStockSparklines';
import { getFullStockConversationHistory } from '@services/history/searchHistoryService';
import { stockDataService } from '@services/api/stockData.service';
import { tickerDetectionService } from '@services/stock/tickerDetectionService';
import { MiniSparkline } from '@components/charts/MiniSparkline';
import { StockAnalysisScreen } from '@/screens/stock/StockAnalysisScreen';

interface WatchlistScreenProps {
  navigation?: any;
}

export const WatchlistScreen: React.FC<WatchlistScreenProps> = ({ navigation }) => {
  const { watchlist, removeFromWatchlist, addToWatchlist, isInWatchlist } = useWatchlist();

  // Extract watchlist tickers for data fetching (memoized to prevent infinite re-renders)
  const watchlistTickers = useMemo(() => watchlist.map(item => item.ticker), [watchlist]);

  // Fetch market data (prices, changes) for watchlist tickers
  const { marketData, refreshData, isLoading } = useMarketData(watchlistTickers);

  // Fetch sparkline chart data for all watchlist tickers
  const { chartDataMap, isLoading: chartsLoading } = useStockSparklines(watchlistTickers);
  const [selectedStock, setSelectedStock] = useState<{
    ticker: string;
    companyName: string;
    previousMessages?: any[];
    stockData?: any;
  } | null>(null);

  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768; // Tablet/desktop breakpoint

  // Resizable panel state (30% of screen width for better layout)
  const [leftPanelWidth, setLeftPanelWidth] = useState(width * 0.3);
  const [isResizing, setIsResizing] = useState(false);
  const dividerRef = useRef<View>(null);

  // Add stock modal state
  const [addModalVisible, setAddModalVisible] = useState(false);

  // Mobile stock detail modal state
  const [mobileModalVisible, setMobileModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ ticker: string; name: string }>>([]);
  const [searching, setSearching] = useState(false);

  // Handle resize with DOM events (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || !isLargeScreen) return;

    const dividerElement = dividerRef.current as any;
    if (!dividerElement) return;

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const newWidth = moveEvent.clientX;
        const minWidth = 300;
        const maxWidth = width * 0.6;
        setLeftPanelWidth(Math.max(minWidth, Math.min(newWidth, maxWidth)));
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    // Attach mousedown listener to the divider element
    dividerElement.addEventListener('mousedown', handleMouseDown);

    return () => {
      dividerElement.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isLargeScreen, width]);

  // Initialize panel width to 30% on large screens
  useEffect(() => {
    if (isLargeScreen && width > 0) {
      const targetWidth = Math.floor(width * 0.3);
      console.log('[WatchlistScreen] Setting panel to 30%:', {
        totalWidth: width,
        leftPanel: targetWidth,
        rightPanel: width - targetWidth,
        leftPercent: '30%',
        rightPercent: '70%',
      });
      setLeftPanelWidth(targetWidth);
    }
  }, [isLargeScreen, width]); // Update when screen size or width changes

  const handleStockPress = async (ticker: string, companyName: string) => {
    // Instant selection for responsiveness
    setSelectedStock({ ticker, companyName });
    if (!isLargeScreen) setMobileModalVisible(true);

    // Fetch in background (analysis can also fetch its own data)
    try {
      const [historyResult, stockData] = await Promise.all([
        getFullStockConversationHistory(ticker),
        stockDataService.getStockData(ticker),
      ]);
      setSelectedStock({
        ticker,
        companyName,
        previousMessages: historyResult.success ? historyResult.messages : undefined,
        stockData,
      });
    } catch {}
  };

  const handleRemove = async (ticker: string) => {
    await removeFromWatchlist(ticker);
  };

  // Search for stocks to add
  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const detection = await tickerDetectionService.detectTicker(query);

      if (detection.ticker && detection.companyName) {
        // Single ticker detected
        setSearchResults([{ ticker: detection.ticker, name: detection.companyName }]);
      } else if (detection.suggestions && detection.suggestions.length > 0) {
        // Multiple suggestions
        setSearchResults(detection.suggestions.map(s => ({ ticker: s.symbol, name: s.name })));
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Add stock from search results
  const handleAddFromSearch = async (ticker: string, name: string) => {
    if (isInWatchlist(ticker)) {
      Alert.alert('Already in Watchlist', `${ticker} is already in your watchlist`);
      return;
    }

    const success = await addToWatchlist(ticker, name);
    if (success) {
      Alert.alert('Added', `${ticker} added to watchlist`);
      setAddModalVisible(false);
      setSearchQuery('');
      setSearchResults([]);
    } else {
      Alert.alert('Error', `Failed to add ${ticker} to watchlist`);
    }
  };

  const renderWatchlistItem = ({ item }: { item: { ticker: string; companyName: string } }) => {
    const liveData = marketData.find(m => m.symbol === item.ticker);

    const isPositive = (liveData?.change || 0) >= 0;
    const changeColor = isPositive ? Colors.market.profit : Colors.market.loss;
    const isSelected = selectedStock?.ticker === item.ticker;

    return (
      <Pressable
        style={[styles.listItem, isSelected && styles.selectedItem]}
        onPress={() => handleStockPress(item.ticker, item.companyName)}
      >
        <View style={styles.leftSection}>
          <Text style={styles.ticker}>{item.ticker}</Text>
          <Text style={styles.companyName} numberOfLines={1}>
            {item.companyName}
          </Text>
        </View>

        {/* Mini chart */}
        <View style={styles.chartSection}>
          {chartDataMap[item.ticker] ? (
            <MiniSparkline data={chartDataMap[item.ticker]} width={60} height={30} />
          ) : (
            <ActivityIndicator size="small" color={Colors.text.tertiary} />
          )}
        </View>

        <View style={styles.rightSection}>
          {liveData ? (
            <>
              <Text style={styles.price}>${liveData.price.toFixed(2)}</Text>
              <Text style={[styles.change, { color: changeColor }]}>
                {isPositive ? '+' : ''}
                {liveData.changePercent.toFixed(2)}%
              </Text>
            </>
          ) : (
            <ActivityIndicator size="small" color={Colors.text.tertiary} />
          )}
        </View>

        <Pressable
          style={styles.removeButton}
          onPress={() => handleRemove(item.ticker)}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="close-circle" size={20} color={Colors.text.tertiary} />
        </Pressable>
      </Pressable>
    );
  };

  // Watchlist content
  const watchlistContent = (
    <View
      style={[styles.container, isLargeScreen && { width: leftPanelWidth, borderRightWidth: 0 }]}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>Watchlist</Text>
          <Text style={styles.headerCount}>{watchlist.length} stocks</Text>
        </View>
        <Pressable onPress={() => setAddModalVisible(true)} style={styles.addButton}>
          <Ionicons name="add-circle" size={28} color={Colors.ai.primary} />
        </Pressable>
      </View>

      {/* Watchlist Content */}
      {watchlist.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="star-outline" size={64} color={Colors.text.tertiary} />
          <Text style={styles.emptyTitle}>No stocks in watchlist</Text>
          <Text style={styles.emptyMessage}>
            Add stocks to your watchlist from the home screen to track them here
          </Text>
        </View>
      ) : (
        <FlatList
          data={watchlist}
          renderItem={renderWatchlistItem}
          keyExtractor={item => item.ticker}
          contentContainerStyle={styles.listContainer}
          refreshControl={
            <RefreshControl
              refreshing={isLoading}
              onRefresh={refreshData}
              tintColor={Colors.ai.primary}
              colors={[Colors.ai.primary]}
            />
          }
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      )}
    </View>
  );

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;
    const prev = document.body.style.overflow;
    if (mobileModalVisible && !isLargeScreen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileModalVisible, isLargeScreen]);

  return (
    <SafeAreaView style={styles.safeArea}>
      {isLargeScreen ? (
        <View style={styles.splitContainer}>
          {/* Left: Watchlist */}
          {watchlistContent}

          {/* Draggable Divider */}
          <View ref={dividerRef} style={[styles.divider, isResizing && styles.dividerActive]}>
            <View style={styles.dividerHandle} />
          </View>

          {/* Right: Stock Detail */}
          <View style={styles.rightPanel}>
            {selectedStock ? (
              <StockAnalysisScreen
                key={selectedStock.ticker}
                route={{
                  params: {
                    ticker: selectedStock.ticker,
                    companyName: selectedStock.companyName,
                    initialTab: 'outlook',
                    sourceType: 'watchlist',
                    stocks: watchlist.map(item => ({
                      ticker: item.ticker,
                      companyName: item.companyName,
                    })),
                  },
                }}
                navigation={navigation}
                hideHeader={true}
                hideStocksList={true}
              />
            ) : (
              <View style={styles.emptyDetailPanel}>
                <Ionicons name="trending-up" size={64} color={Colors.text.tertiary} />
                <Text style={styles.emptyDetailText}>Select a stock to view details</Text>
              </View>
            )}
          </View>
        </View>
      ) : (
        watchlistContent
      )}

      {/* Add Stock Modal */}
      <Modal
        visible={addModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setAddModalVisible(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalContainer}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setAddModalVisible(false)} />
          <View style={styles.modalContent}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Stock to Watchlist</Text>
              <Pressable onPress={() => setAddModalVisible(false)} style={styles.modalCloseButton}>
                <Ionicons name="close" size={24} color={Colors.text.tertiary} />
              </Pressable>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={Colors.text.tertiary} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search by symbol or company name..."
                placeholderTextColor={Colors.text.tertiary}
                value={searchQuery}
                onChangeText={handleSearch}
                autoCapitalize="characters"
                autoCorrect={false}
                autoFocus={true}
              />
              {searching && <ActivityIndicator size="small" color={Colors.ai.primary} />}
            </View>

            {/* Search Results */}
            <FlatList
              data={searchResults}
              keyExtractor={item => item.ticker}
              renderItem={({ item }) => (
                <Pressable
                  style={styles.searchResultItem}
                  onPress={() => handleAddFromSearch(item.ticker, item.name)}
                >
                  <View style={styles.searchResultInfo}>
                    <Text style={styles.searchResultTicker}>{item.ticker}</Text>
                    <Text style={styles.searchResultName} numberOfLines={1}>
                      {item.name}
                    </Text>
                  </View>
                  <View style={styles.searchResultAction}>
                    {isInWatchlist(item.ticker) ? (
                      <Ionicons name="checkmark-circle" size={24} color={Colors.status.success} />
                    ) : (
                      <Ionicons name="add-circle-outline" size={24} color={Colors.ai.primary} />
                    )}
                  </View>
                </Pressable>
              )}
              ListEmptyComponent={
                searchQuery.trim() ? (
                  <View style={styles.emptySearchContainer}>
                    <Ionicons name="search-outline" size={48} color={Colors.text.tertiary} />
                    <Text style={styles.emptySearchText}>
                      {searching ? 'Searching...' : 'No results found'}
                    </Text>
                  </View>
                ) : (
                  <View style={styles.emptySearchContainer}>
                    <Ionicons name="business-outline" size={48} color={Colors.text.tertiary} />
                    <Text style={styles.emptySearchText}>Search for a stock to add</Text>
                  </View>
                )
              }
              style={styles.searchResultsList}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Mobile Stock Detail Modal */}
      <Modal
        visible={mobileModalVisible && !isLargeScreen}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setMobileModalVisible(false)}
      >
        <SafeAreaView style={styles.mobileModalContainer}>
          {/* Modal Header with Close Button */}
          <View style={styles.mobileModalHeader}>
            <Pressable onPress={() => setMobileModalVisible(false)} style={styles.modalCloseButton}>
              <Ionicons name="close" size={28} color={Colors.text.primary} />
            </Pressable>
            <Text style={styles.modalHeaderTitle}>Watchlist</Text>
            <View style={{ width: 28 }} />
          </View>

          {/* Horizontal Stock Chips for Quick Switching */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.stockChipsContainer}
            contentContainerStyle={styles.stockChipsContent}
          >
            {watchlist.map((item, index) => {
              const isSelected = selectedStock?.ticker === item.ticker;
              const liveData = marketData.find(m => m.symbol === item.ticker);

              return (
                <Pressable
                  key={item.ticker}
                  style={[styles.stockChip, isSelected && styles.stockChipSelected]}
                  onPress={() => handleStockPress(item.ticker, item.companyName)}
                >
                  <Text
                    style={[styles.stockChipTicker, isSelected && styles.stockChipTickerSelected]}
                  >
                    {item.ticker}
                  </Text>
                  {liveData && (
                    <Text
                      style={[styles.stockChipPrice, isSelected && styles.stockChipPriceSelected]}
                    >
                      ${liveData.price.toFixed(2)}
                    </Text>
                  )}
                </Pressable>
              );
            })}
          </ScrollView>

          {/* Stock Analysis Content */}
          <View style={styles.mobileModalContent}>
            {selectedStock && (
              <StockAnalysisScreen
                key={selectedStock.ticker}
                route={{
                  params: {
                    ticker: selectedStock.ticker,
                    companyName: selectedStock.companyName,
                    initialTab: 'outlook',
                    sourceType: 'watchlist',
                    stocks: watchlist.map(item => ({
                      ticker: item.ticker,
                      companyName: item.companyName,
                    })),
                  },
                }}
                navigation={navigation}
                hideHeader={true}
                hideStocksList={true}
              />
            )}
          </View>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  container: {
    flex: 1,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPanel: {
    width: 400,
    borderRightWidth: 1,
    borderRightColor: Colors.border.default,
  },
  rightPanel: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  emptyDetailPanel: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyDetailText: {
    fontSize: 16,
    color: Colors.text.secondary,
    marginTop: 16,
    textAlign: 'center',
  },
  divider: {
    width: 6,
    backgroundColor: Colors.border.default,
    ...(Platform.OS === 'web' && {
      cursor: 'col-resize',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    }),
    alignItems: 'center',
    justifyContent: 'center',
  } as any,
  dividerActive: {
    backgroundColor: Colors.accent.primary,
  },
  dividerHandle: {
    width: 2,
    height: 40,
    backgroundColor: Colors.text.tertiary,
    borderRadius: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.default,
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  headerCount: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  addButton: {
    padding: 8,
  },
  listContainer: {
    paddingVertical: 8,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: Colors.background.primary,
  },
  selectedItem: {
    backgroundColor: Colors.background.secondary,
    borderLeftWidth: 3,
    borderLeftColor: Colors.accent.primary,
  },
  leftSection: {
    flex: 1,
    marginRight: 12,
  },
  ticker: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  companyName: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  chartSection: {
    width: 60,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rightSection: {
    alignItems: 'flex-end',
    minWidth: 80,
  },
  price: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  change: {
    fontSize: 12,
    fontWeight: '600',
  },
  removeButton: {
    marginLeft: 12,
    padding: 4,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginLeft: 24,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  // Modal styles
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: Colors.background.secondary,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  modalCloseButton: {
    padding: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 20,
    marginVertical: 16,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: Colors.text.primary,
    padding: 0,
  },
  searchResultsList: {
    flex: 1,
  },
  searchResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  searchResultInfo: {
    flex: 1,
    marginRight: 12,
  },
  searchResultTicker: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.ai.primary,
    marginBottom: 2,
  },
  searchResultName: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  searchResultAction: {
    padding: 4,
  },
  emptySearchContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptySearchText: {
    fontSize: 16,
    color: Colors.text.secondary,
    marginTop: 16,
    textAlign: 'center',
  },
  // Mobile modal styles
  mobileModalContainer: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  mobileModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  modalHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  stockChipsContainer: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    flexGrow: 0,
    flexShrink: 0,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: Colors.background.primary,
      touchAction: 'pan-x',
      overscrollBehaviorX: 'contain',
    }),
  } as any,
  stockChipsContent: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 6,
  },
  stockChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 18,
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.default,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 64,
    height: 44,
  },
  stockChipSelected: {
    backgroundColor: Colors.ai.primary,
    borderColor: Colors.ai.primary,
  },
  stockChipTicker: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  stockChipTickerSelected: {
    color: Colors.background.primary,
  },
  stockChipPrice: {
    fontSize: 11,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  stockChipPriceSelected: {
    color: Colors.background.primary,
    opacity: 0.9,
  },
  mobileModalContent: {
    flex: 1,
  },
});
